import { Check } from 'lucide-react';
import { useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { POPUP_BACKDROP_LEVEL, POPUP_LEVEL } from '@/components/ui/popup-level';

/**
 * Flow's grouped picker: a trigger, and a portaled combobox over grouped options.
 *
 * Lifted out of the target-task picker, which had the only copy. A feedback condition needs the
 * same control for the same reason — a closed set of options that are grouped (by block, as the
 * task picker groups by pathway), too many to scan, and carrying a symbol a native `<option>`
 * cannot render. `SelectField`'s own doc names that limit and points here.
 *
 * What it owns: the query, the filtering, the flattened index the arrow keys walk, the roving
 * `aria-activedescendant`, click-outside, Escape, and placing the portal. What the caller owns: the
 * trigger, the sections, and what choosing one means.
 */

export interface PickerOption {
  key: string;
  /** Plain text, used for filtering and as the accessible name. */
  text: string;
  /** What actually renders, when the row is more than its text (an evaluation glyph and a label). */
  node?: React.ReactNode;
  /** Leading icon, for a row that names an action rather than a thing. */
  icon?: React.ReactNode;
  selected?: boolean;
  /** Why it cannot be chosen. Greyed, announced, and skipped by the keyboard — an option that
   *  vanishes reads as a bug, one that is greyed with a reason answers the question. */
  disabled?: string;
}

export interface PickerSection {
  header?: string;
  /**
   * Keep the header's own capitalisation instead of upper-casing it.
   *
   * For a header that is a NAME rather than a category word. Flow's headers are categories —
   * "Correct", "This task" — and upper-casing reads as a label. A variable set's header is the
   * teacher's own identifier, where `starA` and `STARA` are not the same string: the members
   * below it are `starA_mass`, and a heading that cannot be typed back is worse than a quiet one.
   */
  headerIsName?: boolean;
  options: PickerOption[];
}

interface TriggerProps {
  ref: React.Ref<HTMLButtonElement>;
  onClick: () => void;
  'aria-haspopup': 'listbox';
  'aria-expanded': boolean;
}

/**
 * Opening the menu from somewhere that is not its trigger — see `openAt`.
 */
export interface PickerHandle {
  /** Open centred on an arbitrary point, typically the text caret. */
  openAt: (rect: DOMRect) => void;
}

export function FlowPickerMenu({ sections, onChoose, ariaLabel, filterPlaceholder, noMatchesLabel, renderTrigger, alignTo, handleRef, onDismiss, returnFocus, className = '' }: {
  /** Unfiltered. The menu filters on each option's `text`. */
  sections: PickerSection[];
  onChoose: (key: string) => void;
  ariaLabel: string;
  /**
   * Omit it and NO search box is drawn.
   *
   * A filter over two fixed options is furniture: it asks you to read a field, decide it is
   * not for you, and move past it, every time. The owner's words on the add-a-task menu were
   * exactly that — it "should not have 'find a task'". The keyboard then lives on the list
   * itself, so the menu is no less operable without it.
   */
  filterPlaceholder?: string;
  noMatchesLabel: string;
  renderTrigger: (props: TriggerProps) => React.ReactNode;
  /**
   * Centre the menu on THIS element instead of on the trigger.
   *
   * For a trigger that is small and off to one side of the thing the menu is about — Add Feedback
   * is a short button at the left of a card, so centring on it hung a 288px menu noticeably off
   * the card's own centre. Passing the card lines the two up. Omitted, the trigger is the anchor,
   * which is right whenever the trigger IS the thing being edited.
   */
  alignTo?: React.RefObject<HTMLElement | null>;
  /**
   * Lets a caller open the menu at a POINT rather than at the trigger.
   *
   * For the `{` shortcut: a teacher typing in a question should get the picker under the
   * caret, not up at the toolbar button, because the eye travel is half the complaint. The
   * menu still renders its trigger and still works by click — this is an additional way in,
   * not a replacement.
   */
  handleRef?: React.RefObject<PickerHandle | null>;
  /** Closed without choosing. The `{` shortcut uses it to leave a literal brace behind. */
  onDismiss?: () => void;
  /** Where focus belongs once the menu closes, when it is not the trigger — see `close`. */
  returnFocus?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  /** Set only for a caret-summoned open; the trigger's own rect is used otherwise. */
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /** The floating panel, so a scroll inside its own list is not read as the page moving. */
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  /**
   * Swallow the click that belongs to the gesture that just dismissed the menu.
   *
   * The backdrop is `inset-0`, so it covers the trigger. A press near the trigger dismisses on
   * pointer-down, the backdrop unmounts, and the click of that same press is then delivered to
   * whatever is under the pointer NOW — which Firefox resolves to the trigger the backdrop had
   * been covering, so it toggles and the menu comes straight back.
   *
   * A time window was the first attempt and it did not hold: the synthesized click after a touch
   * can arrive well beyond any window short enough to be safe. This waits for the actual click
   * instead, swallows it only if it landed on the trigger, and stands down on the first click
   * either way — so a deliberate press anywhere else is never eaten.
   *
   * Capture phase on `document`, so it runs before React's own handler on the trigger.
   */
  const disarmSwallow = useRef<(() => void) | null>(null);
  const swallowTriggerClick = useCallback(() => {
    /*
     * ONCE per gesture. One press reaches both the backdrop's own handler and the document
     * listener, and arming twice left a second swallow in place after the first stood down — so
     * the teacher's NEXT press on the trigger was eaten and the menu would not reopen.
     */
    if (disarmSwallow.current) return;
    const onClick = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) { e.stopPropagation(); e.preventDefault(); }
      stop();
    };
    const stop = () => {
      document.removeEventListener('click', onClick, true);
      window.clearTimeout(timer);
      disarmSwallow.current = null;
    };
    // A dismiss that never produces a click at all must not leave the listener armed.
    const timer = window.setTimeout(stop, 1000);
    disarmSwallow.current = stop;
    document.addEventListener('click', onClick, true);
  }, []);

  // Never outlive the component: a swallow armed as the menu unmounts would otherwise sit on
  // `document` eating a click aimed at whatever replaced it.
  useEffect(() => () => disarmSwallow.current?.(), []);

  useImperativeHandle(handleRef, () => ({
    openAt: (rect: DOMRect) => { setAnchorRect(rect); setQuery(''); setOpen(true); },
  }), []);
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;

  const q = query.trim().toLowerCase();
  const shown = useMemo(() => sections
    .map((s) => ({ ...s, options: s.options.filter((o) => !q || o.text.toLowerCase().includes(q)) }))
    .filter((s) => s.options.length > 0), [sections, q]);
  const flat = useMemo(() => shown.flatMap((s) => s.options), [shown]);

  useEffect(() => {
    if (!open) return undefined;
    /*
     * Open WHERE YOU ARE, not at the top of the first group.
     *
     * This always started at row 0, so opening the jump menu from the third task of a pathway
     * put you at the top of the main sequence and asked you to find your way back. The current
     * item is already marked `selected` by both callers, so it is the honest starting point —
     * and the section scroll below brings its heading with it, rather than leaving the row
     * stranded at the bottom edge with no idea which group it belongs to.
     */
    const selected = flat.findIndex((o) => o.selected && !o.disabled);
    setActiveIdx(selected === -1 ? 0 : selected);
    /*
     * Bring the GROUP into view, not just the row.
     *
     * `scrollIntoView({ block: 'nearest' })` on the row alone does the least it can get away
     * with, which lands a selected row at the bottom edge with its heading off-screen — so the
     * menu opens showing a list of tasks with no clue which pathway they are. Scrolling the
     * section instead opens at that pathway's heading, which is what "where am I" looks like.
     *
     * Runs on TOUCH too, unlike the focus below: knowing where you are matters at least as much
     * when you cannot see a highlight.
     */
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const r = requestAnimationFrame(() => {
      if (selected > 0) {
        const row = listRef.current?.querySelector(`#${CSS.escape(`${baseId}-opt-${selected}`)}`);
        row?.closest('li[data-picker-section]')?.scrollIntoView({ block: 'start' });
      }
      // On touch, auto-focusing the filter input pops the soft keyboard, which covers the option
      // list and makes the menu look broken. Skip it there (it is a keyboard convenience; you tap).
      if (!coarse) (inputRef.current ?? listRef.current)?.focus();
    });
    return () => cancelAnimationFrame(r);
    // `flat` deliberately absent: this picks a starting row when the menu OPENS, and re-running
    // it as the filter narrows would drag the highlight around under the typist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Clamp into range AND off anything greyed — filtering down to a disabled option would otherwise
  // leave the highlight parked where Enter does nothing.
  useEffect(() => {
    setActiveIdx((i) => {
      const clamped = Math.min(i, Math.max(0, flat.length - 1));
      if (!flat[clamped]?.disabled) return clamped;
      const forward = flat.findIndex((o, j) => j >= clamped && !o.disabled);
      if (forward !== -1) return forward;
      for (let j = clamped - 1; j >= 0; j -= 1) if (!flat[j]?.disabled) return j;
      return clamped;
    });
  }, [flat]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: Event) => {
      const tgt = e.target as Node;
      if (listRef.current?.parentElement?.contains(tgt) || triggerRef.current?.contains(tgt)) return;
      swallowTriggerClick();
      setOpen(false);
    };
    /*
     * BOTH, because `pointerdown` alone was not enough.
     *
     * The menu stayed open in Firefox at phone width even though a synthetic `pointerdown` on the
     * backdrop dismissed it perfectly — which says the handler was fine and the event never
     * arrived. Rather than keep guessing which engine delivers what for a tap, listen for the
     * other name for the same gesture too. Whichever lands first closes the menu; the second is a
     * no-op because the effect has already torn down.
     */
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [open, swallowTriggerClick]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`#${CSS.escape(`${baseId}-opt-${activeIdx}`)}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open, baseId]);

  /**
   * A menu whose TRIGGER is not on screen must not be on screen either.
   *
   * The portal escapes its parent's `display:none`, and `triggerRect` reads a hidden trigger's
   * rect as all zeroes — so such a menu renders in the top-left corner, anchored to nothing, over
   * a full-screen backdrop. It looks stuck: the control that would toggle it shut is invisible,
   * and the only way out is tapping a transparent backdrop most people would not think to try.
   *
   * Reachable because the task stepper is mounted TWICE — the nav bar's copy and the title bar's,
   * one hidden by a breakpoint rather than unmounted. Rotating a phone moves which of the two is
   * the hidden one, so an open menu can be orphaned mid-interaction; hence closing on resize as
   * well, which is also the honest response to a menu positioned against a viewport that no
   * longer exists.
   *
   * ONLY when the trigger is what opened it. Every reason above is about a menu that has no
   * anchor; a caret-summoned one carries its own rect, which `triggerRect` already prefers, so
   * it is positioned and dismissible whether a trigger exists or not. Without this exemption the
   * rule read `isHidden(null) === true` as an orphan and shut the menu in the same tick it
   * opened, which is what made the `{` shortcut dead in every toolbar-less field.
   */
  useEffect(() => {
    if (!open) return undefined;
    if (anchorRect === null && isHidden(triggerRef.current)) { setOpen(false); return undefined; }
    const onResize = () => setOpen(false);
    /*
     * A SCROLL IS A RESIZE AS FAR AS THIS MENU IS CONCERNED.
     *
     * The position is computed once, at open, from the trigger's rect or from the caret rect
     * captured when `{` or `[` was swallowed — both viewport coordinates, both stale the moment
     * anything between here and the viewport scrolls. The panel is `position: fixed` and
     * portaled to the body, so it does not move with them: it just sits where the anchor used
     * to be. Reported as the menu "showing up way off the modal", and intermittent because it
     * needs a scroll AFTER the open — which the exit editor produces on its own, since its list
     * is a 400px band and putting a caret near the bottom of it scrolls the row into view.
     *
     * Closed rather than repositioned, which is the same answer `onResize` already gives for
     * the same reason: a caret rect cannot be re-derived once the caret has moved, and a menu
     * anchored to a point that no longer exists is worse than no menu.
     *
     * CAPTURE, because scroll does not bubble — a listener on `document` hears nothing
     * otherwise. That also means it hears the menu's OWN list scrolling, which is not the page
     * moving under it and must not close anything.
     */
    const onScroll = (e: Event) => {
      const target = e.target as Node | null;
      if (target && menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, anchorRect]);

  /**
   * Closing WITHOUT choosing.
   *
   * Focus goes back to the trigger when the trigger is what opened it, and nowhere when the
   * caret did — returning focus to a toolbar button a teacher never pressed would throw them
   * out of the sentence they were writing. `onDismiss` is how the caller puts things back:
   * the `{` shortcut uses it to leave the brace it swallowed.
   */
  const close = (opts?: { chose?: boolean }) => {
    const fromCaret = anchorRect !== null;
    setOpen(false);
    setQuery('');
    setAnchorRect(null);
    if (fromCaret) { if (!opts?.chose) onDismiss?.(); }
    /*
     * `returnFocus` OVERRIDES the trigger, for a menu whose trigger is not where the work is.
     *
     * A toolbar button is a fine place to send focus back to, because the surface it edits is
     * a whole document and the teacher's next move is anywhere. An equation input is one line
     * they were mid-way through typing, and landing on the button beside it means reaching for
     * the mouse to get back — so the field asks for its caret instead. It also covers the
     * caret-opened case, where focus was left nowhere at all.
     */
    if (returnFocus) returnFocus();
    else if (!fromCaret) triggerRef.current?.focus();
  };

  function step(from: number, dir: 1 | -1): number {
    for (let i = from + dir; i >= 0 && i < flat.length; i += dir) if (!flat[i]?.disabled) return i;
    return from;
  }
  const firstEnabled = () => { const i = flat.findIndex((o) => !o.disabled); return i === -1 ? 0 : i; };
  const lastEnabled = () => { for (let i = flat.length - 1; i >= 0; i -= 1) if (!flat[i]?.disabled) return i; return 0; };

  function choose(opt: PickerOption) {
    if (opt.disabled) return;
    onChoose(opt.key);
    /* `chose` suppresses `onDismiss`: the two outcomes are "you picked something" and "you did
       not", and a caller that undoes its own trigger on dismissal — the `{` shortcut putting
       the brace back — must not also undo it after a successful pick. */
    close({ chose: true });
  }

  function onInputKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => step(i, 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => step(i, -1)); }
    else if (e.key === 'Home') { e.preventDefault(); setActiveIdx(firstEnabled()); }
    else if (e.key === 'End') { e.preventDefault(); setActiveIdx(lastEnabled()); }
    else if (e.key === 'Enter') { e.preventDefault(); const o = flat[activeIdx]; if (o) choose(o); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
  }

  let counter = -1; // running index across sections → matches `flat`

  return (
    <div className={`relative flex min-w-0 items-center gap-1 ${className}`}>
      {renderTrigger({
        ref: triggerRef,
        /*
         * A plain toggle. The click that belongs to a DISMISS never reaches here — it is swallowed
         * at `document` capture by `swallowTriggerClick`, which is armed the moment the menu is
         * dismissed from outside. See the ref's own note for why that is not a time window.
         */
        onClick: () => setOpen((o) => !o),
        'aria-haspopup': 'listbox',
        'aria-expanded': open,
      })}

      {/*
        * A BACKDROP, not just a document listener, because the listener did not reach touch.
        *
        * `pointerdown` on `document` dismisses reliably with a mouse and did not on a tablet,
        * and chasing which engine delivers what for a tap on an inert region is the wrong fight:
        * a real element under the menu cannot be missed. `ModalShell` already dismisses this way
        * for the same reason.
        *
        * Transparent and unlabelled — it exists to be hit, not seen. One `z` below the menu, and
        * its OWN portal so the menu's markup below is untouched. The document listener stays as
        * well: it also covers a click landing on browser chrome rather than on the page.
        */}
      {open && createPortal(
        <div
          data-noedit
          aria-hidden='true'
          onPointerDown={() => { swallowTriggerClick(); setOpen(false); }}
          onMouseDown={() => { swallowTriggerClick(); setOpen(false); }}
          onTouchStart={() => { swallowTriggerClick(); setOpen(false); }}
          className={`fixed inset-0 ${POPUP_BACKDROP_LEVEL}`}
        />,
        document.body,
      )}

      {open && createPortal(
        <div
          ref={menuRef}
          data-noedit
          style={triggerRect(triggerRef.current, alignTo?.current ?? null, anchorRect, filterPlaceholder === undefined)}
          /* `border-strong`, not the ordinary border, and it is not decoration: this floats OVER
             the flow panel, whose cards and rows are all outlined in `ws-border`. Matching them
             made the menu read as another card in the stack rather than as something on top of it,
             and a shadow alone does not separate them on a light ground. Same treatment the block
             options drawer and the simulator sheet already use for the same reason. */
          className={`fixed ${POPUP_LEVEL} flex max-h-[min(60vh,360px)] flex-col overflow-hidden rounded-lg border border-ws-border-strong bg-ws-elevated shadow-xl`}
        >
          {filterPlaceholder !== undefined && (
          <div className='border-b border-ws-border p-1.5'>
            {/* eslint-disable-next-line no-restricted-syntax -- a combobox, not a field: it carries
                role/aria-activedescendant/aria-autocomplete and its own key handling, none of which
                TextField exposes. */}
            <input
              ref={inputRef}
              type='text'
              role='combobox'
              aria-expanded='true'
              aria-controls={listboxId}
              aria-activedescendant={flat.length ? `${baseId}-opt-${activeIdx}` : undefined}
              aria-autocomplete='list'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onInputKeyDown}
              placeholder={filterPlaceholder}
              aria-label={filterPlaceholder}
              className='w-full rounded-md border border-ws-border bg-ws-surface px-2 py-1.5 text-sm text-ws-text placeholder:text-ws-muted focus:border-ws-primary focus:outline-none'
            />
          </div>
          )}
          {/* `pb-1`, NOT `py-1`: padding above the scroller leaves a strip that a sticky `top-0`
              header cannot cover, so rows scrolled past showed through above it. */}
          {/* With no input to type into, the listbox is the focused element and takes the same
              handler — arrows and Enter behave identically either way. */}
          <ul ref={listRef} role='listbox' id={listboxId} aria-label={ariaLabel}
            tabIndex={filterPlaceholder === undefined ? -1 : undefined}
            onKeyDown={filterPlaceholder === undefined ? onInputKeyDown : undefined}
            aria-activedescendant={filterPlaceholder === undefined && flat.length
              ? `${baseId}-opt-${activeIdx}` : undefined}
            className='min-h-0 flex-1 overflow-auto pb-1 focus:outline-none'>
            {flat.length === 0 && <li className='px-3 py-2 text-xs italic text-ws-muted'>{noMatchesLabel}</li>}
            {/* Sections are keyed by POSITION, with the header only as a readability aid. A
                header is a unique category word for Flow, but `headerIsName` sections carry the
                teacher's own names — two tasks called "dup probe 2 (copy)", two variable sets
                called the same thing — and React warned about duplicate keys the moment an
                experience held two tasks with one title, which duplication makes routine. */}
            {shown.map((sec, si) => (
              <li key={`sec-${si}-${sec.header ?? ''}`} role='none' data-picker-section=''>
                {/* A shaded band, not just small caps: a header set in the same muted type as the
                    rows reads as one of them. Sticky so the group is still named once scrolled. */}
                {sec.header && (
                  <div className={`sticky top-0 z-10 border-y border-ws-border bg-ws-btn px-3 py-1 text-[10px] font-semibold tracking-wide text-ws-muted ${sec.headerIsName ? 'font-mono' : 'uppercase'}`}>
                    {sec.header}
                  </div>
                )}
                <ul role='none'>
                  {sec.options.map((o) => {
                    counter += 1;
                    const idx = counter;
                    /* TWO LINES. A banked block's condition reads "(Any Version, Any Branch)
                       Default Incorrect", and the scope tag is load-bearing — truncating it
                       is what makes "(Version 1) Chose Positive" readable as "everyone who
                       chose Positive". Two is the cap: this is a scrolling list of options
                       and a row that grows without bound stops being scannable. */
                    const body = (
                      <>
                        {o.icon}
                        <span className='min-w-0 flex-1 line-clamp-2 break-words'>{o.node ?? o.text}</span>
                      </>
                    );
                    /* MIDDLE-ALIGNED, which `items-start` was not. A row that carries its source
                       under its name is two lines tall, and a glyph pinned to the top of it reads
                       as belonging to the first line rather than to the row — "middle align the
                       symbol with the alias in the menu". The recall picker, which has had the
                       stacked shape all along, centres for the same reason. */
                    return (
                      // Keyboard is handled centrally by the combobox input (arrows + Enter via
                      // aria-activedescendant), the standard listbox pattern — options need no own
                      // key handler.
                       
                      <li
                        key={o.key}
                        id={`${baseId}-opt-${idx}`}
                        role='option'
                        aria-selected={!!o.selected}
                        aria-disabled={o.disabled ? true : undefined}
                        onMouseEnter={() => { if (!o.disabled) setActiveIdx(idx); }}
                        onClick={() => choose(o)}
                        className={`flex items-center gap-2 px-3 py-1.5 text-sm ${o.disabled ? 'cursor-default' : 'cursor-pointer'} ${idx === activeIdx && !o.disabled ? 'bg-ws-hover' : ''} ${o.selected ? 'font-semibold text-ws-primary dark:text-ws-text-link' : 'text-ws-text'}`}
                      >
                        {/*
                          * THE REASON IS ON THE ROW, not in a tooltip.
                          *
                          * `disabled` has always carried a REASON rather than a flag, on the
                          * stated grounds that "one that is greyed with a reason answers the
                          * question" — and the reason went to `title` and an `sr-only`, so the
                          * only person it ever reached was one using a screen reader or one who
                          * thought to hover. Reported from the scenario link menu, where an
                          * alias over two Numericals is dim with nothing said: "eaten isn't
                          * selectable ... we need more information here".
                          *
                          * The DIMMING STOPS AT THE LABEL. Greying is what says the row cannot
                          * be chosen, and `opacity` inherits — on the `li` it took the
                          * explanation down to 40% with it, which is the one line on the row
                          * that has to be read.
                          */}
                        {o.disabled ? (
                          <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                            <span className='flex min-w-0 items-center gap-2 opacity-40'>{body}</span>
                            {/* The same muted italic the recall picker gives a row's source line:
                                it is an aside about the row, not a second label competing with it
                                — "i like the explanation ... but it overwhelms a bit". */}
                            <span className='text-[0.6875rem] italic leading-tight text-ws-muted'>
                              {t('task.flow.card.optionUnavailable', { reason: o.disabled })}
                            </span>
                          </span>
                        ) : body}
                        {o.selected && <Check className='h-4 w-4 shrink-0' aria-hidden='true' />}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * Where the popup goes: CENTRED on its trigger, clamped on-screen, flipped above when a short
 * viewport has no room below — otherwise it runs off the bottom of a phone.
 *
 * Centred rather than left-aligned, and wider than it was. Left-aligning a 256px menu under a
 * trigger that is itself indented inside a 352px panel hung it off to one side of everything around
 * it, and 256px is narrow for a feedback name that states a whole range ("320° to 360°, 0° to 40°").
 * Width grows with the trigger up to a cap so a wide trigger gets a wide menu, and the cap keeps it
 * from spanning a desktop.
 */
const MENU_MIN_W = 288;
/**
 * A menu with no filter is a short list of short labels, and 288px is a width sized for
 * something else: room to type, over task names that run long. On a `+` at the end of a pager
 * it draws a panel several times the width of anything in it.
 *
 * The filter is the signal, not a separate prop — a menu that needs typing room is exactly the
 * menu that has somewhere to type.
 */
const MENU_COMPACT_MIN_W = 168;
const MENU_MAX_W = 420;

/**
 * Whether an element is not rendered at all — `display:none`, on it or on an ancestor.
 *
 * Deliberately NOT a layout probe (`getClientRects().length`, `offsetParent`): jsdom lays nothing
 * out, so every element looks hidden to those and a guard built on one is both wrong in tests and
 * untestable. `checkVisibility` asks the question directly where it exists; the computed-style
 * fallback answers it everywhere else, including for the inline style a test can set.
 */
function isHidden(el: HTMLElement | null): boolean {
  if (!el) return true;
  if (typeof el.checkVisibility === 'function') return !el.checkVisibility();
  return getComputedStyle(el).display === 'none';
}

/**
 * THE BOX THE MENU MUST STAY INSIDE — the nearest SCROLLING ancestor, or the viewport.
 *
 * Clamping to the window alone let a menu hang past the edge of the panel it opened in and sit
 * on top of that panel's scrollbar, so the editor could not be scrolled while it was open.
 * Measured in the block editor at 1582px: the panel ran 1183–1565 with a 15px scrollbar, and
 * the menu opened 1286–1574.
 *
 * `clientWidth` is the measurement that matters, because it stops at the scrollbar — which is
 * precisely the strip the menu must not cover.
 *
 * A container too narrow to hold the menu gives it back to the viewport: something has to
 * overflow, and a menu squeezed inside a 200px rail is less usable than one that overhangs it.
 */
function containerBounds(el: Element | null): { left: number; right: number } | null {
  for (let n = el?.parentElement ?? null; n; n = n.parentElement) {
    const style = getComputedStyle(n);
    if (!/(auto|scroll|overlay)/.test(`${style.overflowY} ${style.overflowX}`)) continue;
    /* Only a container that is ACTUALLY scrolling. `overflow-auto` on something short draws no
       scrollbar and has no edge worth respecting — and in jsdom nothing lays out, so every
       candidate reports zero and this falls through to the viewport. */
    if (n.scrollHeight <= n.clientHeight && n.scrollWidth <= n.clientWidth) continue;
    const r = n.getBoundingClientRect();
    return { left: r.left + n.clientLeft, right: r.left + n.clientLeft + n.clientWidth };
  }
  return null;
}

function triggerRect(el: HTMLButtonElement | null, alignTo: HTMLElement | null, anchorRect: DOMRect | null | undefined, compact: boolean): React.CSSProperties {
  // A caret-summoned open hangs off the CARET, which is the whole point of it.
  const r = anchorRect ?? el?.getBoundingClientRect();
  if (!r) return { top: 0, left: 0 };
  // Vertically the menu always hangs off the TRIGGER — it has to open where the finger is. Only
  // the horizontal centre comes from `alignTo`, which is the part that looked wrong.
  const centreOn = anchorRect ?? alignTo?.getBoundingClientRect() ?? r;
  const minW = compact ? MENU_COMPACT_MIN_W : MENU_MIN_W;
  const width = Math.min(MENU_MAX_W, Math.max(minW, centreOn.width), Math.max(minW, window.innerWidth - 16));
  const maxH = Math.min(360, window.innerHeight * 0.6);
  const centred = centreOn.left + centreOn.width / 2 - width / 2;
  const bounds = containerBounds(el);
  /* The panel first, the viewport as the floor — and the panel only while it has room, so a
     menu is never squeezed into a rail narrower than itself. */
  const fits = bounds !== null && bounds.right - bounds.left >= width + 16;
  const minLeft = fits ? Math.max(8, bounds.left + 8) : 8;
  const maxLeft = fits
    ? Math.min(window.innerWidth - width - 8, bounds.right - width - 8)
    : window.innerWidth - width - 8;
  const left = Math.max(minLeft, Math.min(centred, Math.max(minLeft, maxLeft)));
  const below = window.innerHeight - r.bottom;
  if (below < maxH + 8 && r.top > below) {
    return { bottom: Math.max(8, window.innerHeight - r.top + 4), left, width, maxHeight: Math.min(maxH, r.top - 12) };
  }
  return { top: r.bottom + 4, left, width, maxHeight: Math.min(maxH, below - 12) };
}
